import { Box, Typography, useTheme, useColorScheme, Input, Tabs, Tab, TabList, TabPanel, Chip, IconButton, Grid, Select, Option, Button, Snackbar } from "@mui/joy";
import { CONTENT_WIDTH } from "../../constants/layout";
import Dialog from "../../components/UI/custom_elements/dialog";
import { PieChart, Pie, Cell, Tooltip as ChartTooltip, Legend, ResponsiveContainer } from "recharts";
import { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useGet from "../../hooks/useGet";
import SessionContext from "../../store";
import HeadTable from "../../components/UI/custom_elements/headTable";
import TableTransactionsReport from "../../components/UI/custom_elements/tableTransactionsReport";

const SearchIcon = ({ color = "#082B39" }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 22L20 20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CopyIcon = ({ color = "#082B39" }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 13.4V16.4C17 20.4 15.4 22 11.4 22H7.6C3.6 22 2 20.4 2 16.4V12.6C2 8.6 3.6 7 7.6 7H10.6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 13.4H13.8C11.4 13.4 10.6 12.6 10.6 10.2V7L17 13.4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.6 2H15.6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 5C7 3.34 8.34 2 10 2H12.62" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 8V14.19C22 15.74 20.74 17 19.19 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 8H19C16.75 8 16 7.25 16 5V2L22 8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronLeftIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronRightIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Status → Spanish label + color (colors match the row status dots:
// green #1AF565 success, red #F20E25 expired, amber #B1993A error/other).
const STATUS_META = {
    success: { label: 'Completado', color: '#1AF565' },
    completed: { label: 'Completado', color: '#1AF565' },
    expired: { label: 'Cotización expirada', color: '#F20E25' },
    awaiting_funds_confirmation: { label: 'Procesando', color: '#F2A20E' },
    pending: { label: 'Pendiente', color: '#3B82F6' },
    failed: { label: 'Fallido', color: '#B1993A' },
    error: { label: 'Error', color: '#B1993A' },
};
const statusMeta = (raw) =>
    STATUS_META[(raw || '').toLowerCase()] || { label: raw || 'Desconocido', color: '#6B7280' };

const TransactionsReportPage = () => {
    const theme = useTheme();
    const { mode, systemMode } = useColorScheme();
    const isDark = (mode === 'system' ? systemMode : mode) === 'dark';
    const { session } = useContext(SessionContext);
    const navigate = useNavigate();

    const isAdmin = session.type === 'admin';

    const [activeTab, setActiveTab] = useState(0);
    const tableScrollRef = useRef(null);
    const isDraggingTableRef = useRef(false);
    const dragStartXRef = useRef(0);
    const dragScrollLeftRef = useRef(0);
    const dragMovedRef = useRef(false);

    const [copySnack, setCopySnack] = useState(false);
    const [csvOpen, setCsvOpen] = useState(false);
    const [csvStart, setCsvStart] = useState('');
    const [csvEnd, setCsvEnd] = useState('');
    const [csvLoading, setCsvLoading] = useState(false);
    const [csvError, setCsvError] = useState('');

    const [monthStats, setMonthStats] = useState({});
    const [monthStatsLoading, setMonthStatsLoading] = useState(false);

    const depositsHeads = ["Tipo", "Monto", "Concepto", "Referencia"];

    const { error: depositsError, isLoading: depositsLoading, get: getDeposits } = useGet(); // eslint-disable-line no-unused-vars

    const [transactions, setTransactions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionsLimit, setTransactionsLimit] = useState(10);
    const [transactionsPagination, setTransactionsPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNext: false,
        hasPrev: false
    });

    // Deposits & Commissions states
    const [depositsCommissions, setDepositsCommissions] = useState([]);
    const [depositsSearch, setDepositsSearch] = useState('');
    const [depositsPage, setDepositsPage] = useState(1);
    const [depositsLimit, setDepositsLimit] = useState(10);
    const [depositsPagination, setDepositsPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNext: false,
        hasPrev: false
    });
    const [depositsLoadingState, setDepositsLoadingState] = useState(false);

    if (depositsError) console.log("Error al cargar depósitos/comisiones:", depositsError);

    const fetchData = async (page = transactionsPage) => {
        setTransactionsLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: transactionsLimit.toString()
            });
            const response = await axios.get(
                `${process.env.REACT_APP_URL_API}/orders/transactions/v2/report?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${session.token}`
                    }
                }
            );

            if (response.data && Array.isArray(response.data.data)) {
                setTransactions(response.data.data);
                if (response.data.pagination) {
                    setTransactionsPagination(response.data.pagination);
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            setTransactionsLoading(false);
        }
    };

    const fetchDepositsCommissions = async (page = depositsPage) => {
        setDepositsLoadingState(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: depositsLimit.toString()
            });
            const response = await axios.get(
                `${process.env.REACT_APP_URL_API}/orders/transactions?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${session.token}`
                    }
                }
            );

            if (response.data && Array.isArray(response.data.data)) {
                setDepositsCommissions(response.data.data);
                if (response.data.pagination) {
                    setDepositsPagination(response.data.pagination);
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            setDepositsLoadingState(false);
        }
    };

    useEffect(() => {
        if (session.token) {
            fetchData(transactionsPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.token, transactionsPage, transactionsLimit]);

    useEffect(() => {
        if (session.token) {
            fetchDepositsCommissions(depositsPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.token, depositsPage, depositsLimit]);

    useEffect(() => {
        if (session.token) {
            fetchMonthStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.token]);

    const filteredTransactions = useMemo(() => {
        if (!searchTerm.trim()) return transactions;
        const lowerSearch = searchTerm.toLowerCase().trim();
        return transactions.filter((transaction) => {
            const externalId = (transaction.external_id || '').toString().toLowerCase();
            const externalRef = (transaction.external_reference || '').toString().toLowerCase();
            const totalTarget = (transaction.total_in_target_currency || '').toString().toLowerCase();
            const feeCurrency = (transaction.platform_fee_currency || '').toString().toLowerCase();
            const feeAmount = (transaction.platform_fee_amount || '').toString().toLowerCase();
            const totalUsdt = (transaction.total_quote_in_usdt || '').toString().toLowerCase();
            const status = (transaction.status || '').toString().toLowerCase();
            const destIdentity = (transaction.receiver_info?.idNumberDestino || '').toString().toLowerCase();
            const phoneNumber = (transaction.receiver_info?.phoneDestino || '').toString().toLowerCase();
            const bankAccount = (transaction.receiver_info?.BankAccount || '').toString().toLowerCase();
            return (
                externalId.includes(lowerSearch) || externalRef.includes(lowerSearch) ||
                totalTarget.includes(lowerSearch) || feeCurrency.includes(lowerSearch) ||
                feeAmount.includes(lowerSearch) || totalUsdt.includes(lowerSearch) ||
                status.includes(lowerSearch) || destIdentity.includes(lowerSearch) ||
                phoneNumber.includes(lowerSearch) || bankAccount.includes(lowerSearch)
            );
        });
    }, [transactions, searchTerm]);

    const showBankReference = useMemo(() => (
        filteredTransactions.some((transaction) => (
            transaction.bank_reference !== null && transaction.bank_reference !== undefined
        ))
    ), [filteredTransactions]);

    const heads = useMemo(() => {
        const baseHeads = [
            "Estatus", "Fecha", "# Identificación", "Teléfono destino", "Banco destino",
            "VES enviados", "id Banco"
        ];

        if (showBankReference) {
            baseHeads.push("Referencia banco");
        }

        if (isAdmin) {
            baseHeads.push("Monto comisión", "id Muney", "Total USDt cargados");
        }

        baseHeads.push("Error", "Recibo");

        return baseHeads;
    }, [isAdmin, showBankReference]);

    const filteredDepositsCommissions = useMemo(() => {
        if (!depositsSearch.trim()) return depositsCommissions;
        const search = depositsSearch.toLowerCase().trim();
        return depositsCommissions.filter(item =>
            (item.type && item.type.toLowerCase().includes(search)) ||
            (item.concept && item.concept.toLowerCase().includes(search)) ||
            (item.reference && item.reference.toLowerCase().includes(search)) ||
            (item.amount && item.amount.toString().includes(search))
        );
    }, [depositsCommissions, depositsSearch]);

    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleDepositsSearchChange = (e) => setDepositsSearch(e.target.value);

    const handleRetry = (transaction) => {
        navigate('/tx', {
            state: {
                retryMode: true,
                transaction
            }
        });
    };

    const truncateToDecimals = (num, decimals) => {
        const factor = Math.pow(10, decimals);
        return Math.trunc(num * factor) / factor;
    };

    const formatAmount = (value) => {
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 6, maximumFractionDigits: 6 }).format(truncateToDecimals(numValue || 0, 6));
    };

    const truncateReference = (ref, maxLength = 20) => {
        if (!ref) return '-';
        if (ref.length <= maxLength) return ref;
        return `${ref.slice(0, maxLength)}...`;
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (e) {
            console.error('Error al copiar');
        }
    };

    // One-click copy of a table cell value — ignored if the press was a drag-scroll.
    const handleCellCopy = (value) => {
        if (dragMovedRef.current) return;
        if (value == null || value === '' || value === '-') return;
        copyToClipboard(String(value));
        setCopySnack(true);
    };

    const CSV_STATUS = {
        success: 'Completado', completed: 'Completado', expired: 'Cotización expirada',
        awaiting_funds_confirmation: 'Procesando', pending: 'Pendiente', failed: 'Fallido',
    };
    const csvCell = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    // Build + download a CSV of the report for the chosen date range (server filters
    // by startDate/endDate; we page through with limit=100 and assemble client-side).
    const downloadCsv = async () => {
        setCsvError('');
        setCsvLoading(true);
        try {
            const headers = [
                'Estatus', 'Fecha', '# Identificación', 'Teléfono destino', 'Banco destino',
                'VES enviados', 'id Banco', 'Referencia banco',
                ...(isAdmin ? ['Monto comisión', 'id Muney', 'Total USDt cargados'] : []),
                'Error',
            ];
            const rows = [];
            let page = 1;
            let totalPages = 1;
            do {
                const params = new URLSearchParams({ page: String(page), limit: '100' });
                if (csvStart) params.append('startDate', csvStart);
                if (csvEnd) params.append('endDate', csvEnd);
                const resp = await axios.get(
                    `${process.env.REACT_APP_URL_API}/orders/transactions/v2/report?${params.toString()}`,
                    { headers: { Authorization: `Bearer ${session.token}` } }
                );
                const data = Array.isArray(resp.data?.data) ? resp.data.data : [];
                totalPages = resp.data?.pagination?.totalPages || 1;
                data.forEach((e) => {
                    rows.push([
                        CSV_STATUS[(e.status || '').toLowerCase()] || e.status || '',
                        e.last_updated_at ? new Date(e.last_updated_at).toLocaleString('es-ES') : '',
                        e.receiver_info?.idNumberDestino || '',
                        e.receiver_info?.phoneDestino || '',
                        e.receiver_info?.BankAccount || '',
                        e.total_in_target_currency != null ? Number(e.total_in_target_currency).toFixed(2) : '',
                        e.external_reference || '',
                        e.bank_reference || '',
                        ...(isAdmin ? [
                            e.platform_fee_amount != null ? `${e.platform_fee_amount} ${e.platform_fee_currency || ''}`.trim() : '',
                            e.external_id || '',
                            e.total_quote_in_usdt != null ? `${e.total_quote_in_usdt} USDT` : '',
                        ] : []),
                        e.error_code || '',
                    ]);
                });
                page += 1;
            } while (page <= totalPages);

            if (rows.length === 0) {
                setCsvError('No hay transacciones en el rango seleccionado.');
                return;
            }

            const csv = [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `transacciones_${csvStart || 'inicio'}_a_${csvEnd || 'hoy'}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            setCsvOpen(false);
        } catch (err) {
            console.error(err);
            setCsvError('No se pudo generar el CSV. Intente de nuevo.');
        } finally {
            setCsvLoading(false);
        }
    };

    // Counts of each status for the ongoing (current) month, for the pie chart.
    const fetchMonthStats = async () => {
        setMonthStatsLoading(true);
        try {
            const now = new Date();
            const y = now.getFullYear();
            const m = now.getMonth(); // 0-based
            const startDate = `${y}-${String(m + 1).padStart(2, '0')}-01`;
            const next = new Date(y, m + 1, 1); // first day of next month (inclusive upper bound)
            const endDate = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
            const counts = {};
            let page = 1;
            let totalPages = 1;
            let guard = 0;
            do {
                const params = new URLSearchParams({ page: String(page), limit: '100', startDate, endDate });
                const resp = await axios.get(
                    `${process.env.REACT_APP_URL_API}/orders/transactions/v2/report?${params.toString()}`,
                    { headers: { Authorization: `Bearer ${session.token}` } }
                );
                const data = Array.isArray(resp.data?.data) ? resp.data.data : [];
                totalPages = resp.data?.pagination?.totalPages || 1;
                data.forEach((e) => {
                    const s = (e.status || 'unknown').toLowerCase();
                    counts[s] = (counts[s] || 0) + 1;
                });
                page += 1;
                guard += 1;
            } while (page <= totalPages && guard < 50);
            setMonthStats(counts);
        } catch (err) {
            console.error('Error al cargar estadísticas del mes', err);
            setMonthStats({});
        } finally {
            setMonthStatsLoading(false);
        }
    };

    const pieData = useMemo(
        () => Object.entries(monthStats)
            .map(([raw, value]) => ({ name: statusMeta(raw).label, value, color: statusMeta(raw).color }))
            .sort((a, b) => b.value - a.value),
        [monthStats]
    );
    const monthTotal = useMemo(() => pieData.reduce((s, d) => s + d.value, 0), [pieData]);

    const handleTableDragStart = (event) => {
        if (!tableScrollRef.current) return;

        isDraggingTableRef.current = true;
        dragMovedRef.current = false;
        dragStartXRef.current = event.pageX - tableScrollRef.current.offsetLeft;
        dragScrollLeftRef.current = tableScrollRef.current.scrollLeft;
        tableScrollRef.current.style.cursor = 'grabbing';
    };

    const handleTableDragMove = (event) => {
        if (!isDraggingTableRef.current || !tableScrollRef.current) return;

        event.preventDefault();
        const x = event.pageX - tableScrollRef.current.offsetLeft;
        const walk = x - dragStartXRef.current;
        // Past a small threshold this is a pan, not a click — suppress click-to-copy.
        if (Math.abs(walk) > 5) dragMovedRef.current = true;
        tableScrollRef.current.scrollLeft = dragScrollLeftRef.current - walk;
    };

    const handleTableDragEnd = () => {
        isDraggingTableRef.current = false;
        if (tableScrollRef.current) {
            tableScrollRef.current.style.cursor = 'grab';
        }
    };

    const getTypeLabel = (type) => {
        switch (type) {
            case 'IN': return 'Depósito';
            case 'OUT': return 'Comisión';
            case 'WITHDRAWAL': return 'Retiro';
            default: return type;
        }
    };

    const getTypeStyles = (type) => {
        if (type === 'IN') {
            return { backgroundColor: '#2E7D32', color: '#FFFFFF' }; // Verde con texto blanco
        }
        if (type === 'OUT') {
            return { backgroundColor: '#94B1FF', color: '#FFFFFF' }; // Azul con texto blanco
        }
        if (type === 'WITHDRAWAL') {
            return { backgroundColor: '#FF9800', color: '#FFFFFF' }; // Naranja con texto blanco
        }
        return { backgroundColor: '#757575', color: '#FFFFFF' };
    };

    const LIMIT_OPTIONS = [10, 25, 50, 100];

    const LimitSelector = ({ value, onChange }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main, whiteSpace: 'nowrap' }}>
                Filas por página:
            </Typography>
            <Select
                value={value}
                onChange={(_, newValue) => onChange(newValue)}
                size="sm"
                sx={{
                    minWidth: 70,
                    fontFamily: "Montserrat",
                    fontSize: "13px",
                    borderRadius: "8px",
                    border: "2px solid #18E9E9",
                    backgroundColor: theme.vars.palette.background.body,
                }}
            >
                {LIMIT_OPTIONS.map((opt) => (
                    <Option key={opt} value={opt} sx={{ fontFamily: "Montserrat", fontSize: "13px" }}>
                        {opt}
                    </Option>
                ))}
            </Select>
        </Box>
    );

    const PaginationControls = ({ pagination, currentPage, setPage }) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
            <IconButton
                variant="outlined"
                disabled={!pagination.hasPrev}
                onClick={() => setPage(currentPage - 1)}
                size="sm"
            >
                <ChevronLeftIcon />
            </IconButton>
            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                Página {pagination.currentPage} de {pagination.totalPages} ({pagination.totalCount} registros)
            </Typography>
            <IconButton
                variant="outlined"
                disabled={!pagination.hasNext}
                onClick={() => setPage(currentPage + 1)}
                size="sm"
            >
                <ChevronRightIcon />
            </IconButton>
        </Box>
    );

    return (
        <>
            <Box position="relative" mx={5} my={2} sx={{ width: CONTENT_WIDTH, overflowX: 'hidden' }}>
                <Box mb={4}>
                    <Typography sx={{
                        fontFamily: "Montserrat", fontSize: "20px", fontStyle: "normal",
                        fontWeight: 700, lineHeight: "120%", color: theme.vars.palette.primary["500"],
                    }}>
                        Historial de Transferencias
                    </Typography>
                </Box>

                <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ width: '100%' }}>
                    <TabList sx={{ mb: 3 }}>
                        <Tab sx={{ fontFamily: "Montserrat", fontWeight: 600, color: theme.vars.palette.primary["500"] }}>Transacciones</Tab>
                        <Tab sx={{ fontFamily: "Montserrat", fontWeight: 600, color: theme.vars.palette.primary["500"] }}>Depósitos y comisiones</Tab>
                    </TabList>

                    {/* Tab Transacciones */}
                    <TabPanel
                        value={0}
                        sx={{
                            p: 4,
                            backgroundColor: theme.vars.palette.background.surface,
                            borderRadius: '12px'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <LimitSelector
                                value={transactionsLimit}
                                onChange={(val) => {
                                    setTransactionsLimit(val);
                                    setTransactionsPage(1);
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => { setCsvError(''); setCsvOpen(true); }}
                                    startDecorator={
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                    }
                                    sx={{
                                        borderRadius: "12px", border: "2px solid #18E9E9", fontFamily: "Montserrat",
                                        fontSize: "14px", fontWeight: 600, whiteSpace: 'nowrap', padding: "8px 16px",
                                        '&:hover': { borderColor: theme.vars.palette.primary["400"] },
                                    }}
                                >
                                    Descargar CSV
                                </Button>
                                <Input
                                    placeholder="Buscar en todas las columnas..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    startDecorator={
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="11" cy="11" r="8"></circle>
                                            <path d="m21 21-4.3-4.3"></path>
                                        </svg>
                                    }
                                    sx={{
                                        width: '350px', padding: "8px 16px", borderRadius: "12px", border: "2px solid #18E9E9",
                                        backgroundColor: theme.vars.palette.background.body, fontFamily: "Montserrat", fontSize: "14px", fontWeight: 500,
                                        '&:hover': { borderColor: theme.vars.palette.primary["400"] },
                                        '&:focus-within': { borderColor: theme.vars.palette.primary["500"], boxShadow: `0 0 0 3px ${theme.vars.palette.primary["100"]}` },
                                    }}
                                />
                            </Box>
                        </Box>

                        {searchTerm && (
                            <Box mb={2}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.secondary }}>
                                    {filteredTransactions.length} resultado{filteredTransactions.length !== 1 ? 's' : ''} encontrado{filteredTransactions.length !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                        )}

                        <Box
                            ref={tableScrollRef}
                            onMouseDown={handleTableDragStart}
                            onMouseMove={handleTableDragMove}
                            onMouseUp={handleTableDragEnd}
                            onMouseLeave={handleTableDragEnd}
                            sx={{
                                mt: 2,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                cursor: 'grab',
                                userSelect: 'none',
                                pb: 1,
                                '&::-webkit-scrollbar': {
                                    display: 'none'
                                },
                                msOverflowStyle: 'none',
                                scrollbarWidth: 'none'
                            }}
                        >
                            <Box sx={{ minWidth: `${Math.max(heads.length * 150, 1200)}px` }}>
                                <Box><HeadTable heads={heads} theme={theme} /></Box>

                                <Box mt={4}>
                                    {transactionsLoading ? (
                                        <Box display="flex" justifyContent="center" alignItems="center" mt={2} sx={{ height: '100%', width: "100%" }}>
                                            <Box display="flex" justifyContent="center" overflow="hidden" sx={{ width: "40px", height: "40px", mx: 1 }}>
                                                <svg aria-hidden="true" style={{ fill: "#9ca3af", color: "#EDF2F7", animation: 'spin 1s linear infinite' }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M10.2319 16.778C10.3291 17.3917 9.90965 17.9754 9.28864 17.9954C8.01444 18.0363 6.74156 17.8061 5.55585 17.3149C4.02239 16.6797 2.69445 15.6329 1.71885 14.2901C0.743241 12.9473 0.157971 11.3608 0.027744 9.70613C-0.102483 8.05145 0.227407 6.39298 0.980941 4.91409C1.73448 3.43519 2.8823 2.19348 4.29751 1.32624C5.71273 0.458995 7.3402 2.9673e-08 9 0C10.6598 -2.9673e-08 12.2873 0.458995 13.7025 1.32624C14.7968 1.99682 15.7312 2.89128 16.4471 3.94617C16.796 4.46028 16.5703 5.14275 16.0167 5.42482V5.42482C15.4631 5.7069 14.7926 5.47939 14.4226 4.98026C13.9097 4.28843 13.2671 3.69827 12.5269 3.24468C11.4655 2.59425 10.2449 2.25 9 2.25C7.75515 2.25 6.53454 2.59425 5.47313 3.24468C4.41172 3.89511 3.55086 4.82639 2.98571 5.93556C2.42056 7.04473 2.17314 8.28859 2.27081 9.5296C2.36848 10.7706 2.80743 11.9604 3.53914 12.9676C4.27084 13.9747 5.26679 14.7598 6.41689 15.2362C7.21892 15.5684 8.07412 15.7414 8.93527 15.7497C9.55656 15.7557 10.1347 16.1644 10.2319 16.778V16.778Z" fill={theme.palette.neutral[500]} />
                                                </svg>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <TableTransactionsReport
                                            elements={filteredTransactions}
                                            theme={theme}
                                            columns={heads.length}
                                            isAdmin={isAdmin}
                                            showBankReference={showBankReference}
                                            onRetry={handleRetry}
                                            onCopyCell={handleCellCopy}
                                        />
                                    )}
                                    {!transactionsLoading && filteredTransactions.length === 0 && (
                                        <Box display="flex" flexDirection="column" alignItems="center" mt={5}>
                                            <Typography level="body-md" sx={{ fontFamily: "Montserrat" }}>
                                                {searchTerm ? `No se encontraron transacciones que coincidan con "${searchTerm}"` : "No se encontraron transacciones."}
                                            </Typography>
                                            {searchTerm && (
                                                <Typography level="body-sm" sx={{ mt: 1, color: theme.vars.palette.primary["500"], cursor: 'pointer', fontFamily: "Montserrat", '&:hover': { textDecoration: 'underline' } }} onClick={() => setSearchTerm('')}>
                                                    Limpiar búsqueda
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        {!transactionsLoading && transactionsPagination.totalPages > 1 && (
                            <PaginationControls
                                pagination={transactionsPagination}
                                currentPage={transactionsPage}
                                setPage={setTransactionsPage}
                            />
                        )}

                        {/* Distribución de estatus del mes en curso */}
                        <Box sx={{ mt: 5, p: 3, backgroundColor: theme.vars.palette.background.body, borderRadius: '12px' }}>
                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 700, color: theme.vars.palette.primary["500"], mb: 0.5 }}>
                                Estatus del mes en curso
                            </Typography>
                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main, opacity: 0.8, mb: 2 }}>
                                {monthStatsLoading ? 'Cargando…' : `${monthTotal} transacción${monthTotal !== 1 ? 'es' : ''} este mes`}
                            </Typography>
                            {!monthStatsLoading && pieData.length === 0 ? (
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                                    No hay transacciones este mes.
                                </Typography>
                            ) : (
                                <ResponsiveContainer width="100%" height={320}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={110}
                                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                        >
                                            {pieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <ChartTooltip formatter={(value, name) => [`${value}`, name]} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </TabPanel>

                    {/* Tab Depósitos y comisiones */}
                    <TabPanel
                        value={1}
                        sx={{
                            p: 4,
                            backgroundColor: theme.vars.palette.background.surface,
                            borderRadius: '12px'
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <LimitSelector
                                value={depositsLimit}
                                onChange={(val) => {
                                    setDepositsLimit(val);
                                    setDepositsPage(1);
                                }}
                            />
                            <Input
                                placeholder="Buscar por tipo, concepto, referencia..."
                                value={depositsSearch}
                                onChange={handleDepositsSearchChange}
                                startDecorator={<SearchIcon color={theme.vars.palette.text.primary} />}
                                sx={{
                                    width: '350px', padding: "8px 16px", borderRadius: "12px", border: "2px solid #18E9E9",
                                    backgroundColor: theme.vars.palette.background.body, fontFamily: "Montserrat", fontSize: "14px", fontWeight: 500,
                                    '&:hover': { borderColor: theme.vars.palette.primary["400"] },
                                    '&:focus-within': { borderColor: theme.vars.palette.primary["500"], boxShadow: `0 0 0 3px ${theme.vars.palette.primary["100"]}` },
                                }}
                            />
                        </Box>

                        {depositsSearch && (
                            <Box mb={2}>
                                <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.secondary }}>
                                    {filteredDepositsCommissions.length} resultado{filteredDepositsCommissions.length !== 1 ? 's' : ''} encontrado{filteredDepositsCommissions.length !== 1 ? 's' : ''}
                                </Typography>
                            </Box>
                        )}

                        <Box mt={2}><HeadTable heads={depositsHeads} theme={theme} /></Box>

                        <Box mt={4}>
                            {depositsLoadingState ? (
                                <Box display="flex" justifyContent="center" alignItems="center" mt={2} sx={{ height: '100%', width: "100%" }}>
                                    <Box display="flex" justifyContent="center" overflow="hidden" sx={{ width: "40px", height: "40px", mx: 1 }}>
                                        <svg aria-hidden="true" style={{ fill: "#9ca3af", color: "#EDF2F7", animation: 'spin 1s linear infinite' }} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M10.2319 16.778C10.3291 17.3917 9.90965 17.9754 9.28864 17.9954C8.01444 18.0363 6.74156 17.8061 5.55585 17.3149C4.02239 16.6797 2.69445 15.6329 1.71885 14.2901C0.743241 12.9473 0.157971 11.3608 0.027744 9.70613C-0.102483 8.05145 0.227407 6.39298 0.980941 4.91409C1.73448 3.43519 2.8823 2.19348 4.29751 1.32624C5.71273 0.458995 7.3402 2.9673e-08 9 0C10.6598 -2.9673e-08 12.2873 0.458995 13.7025 1.32624C14.7968 1.99682 15.7312 2.89128 16.4471 3.94617C16.796 4.46028 16.5703 5.14275 16.0167 5.42482V5.42482C15.4631 5.7069 14.7926 5.47939 14.4226 4.98026C13.9097 4.28843 13.2671 3.69827 12.5269 3.24468C11.4655 2.59425 10.2449 2.25 9 2.25C7.75515 2.25 6.53454 2.59425 5.47313 3.24468C4.41172 3.89511 3.55086 4.82639 2.98571 5.93556C2.42056 7.04473 2.17314 8.28859 2.27081 9.5296C2.36848 10.7706 2.80743 11.9604 3.53914 12.9676C4.27084 13.9747 5.26679 14.7598 6.41689 15.2362C7.21892 15.5684 8.07412 15.7414 8.93527 15.7497C9.55656 15.7557 10.1347 16.1644 10.2319 16.778V16.778Z" fill={theme.palette.neutral[500]} />
                                        </svg>
                                    </Box>
                                </Box>
                            ) : filteredDepositsCommissions.length > 0 ? (
                                filteredDepositsCommissions.map((item, index) => (
                                    <Grid key={item.reference + '-' + index} container spacing={2} mb={2} columns={4} sx={{ borderRadius: "12px", padding: "16px 12px", backgroundColor: theme.vars.palette.card || "#FFFFFF", alignItems: "center" }}>
                                        <Grid xs={1} display="flex" justifyContent="center">
                                            <Chip size="sm" variant="soft" sx={getTypeStyles(item.type)}>
                                                {getTypeLabel(item.type)}
                                            </Chip>
                                        </Grid>
                                        <Grid xs={1} display="flex" justifyContent="center">
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: theme.vars.palette.primary["500"] }}>
                                                {formatAmount(item.amount)}
                                            </Typography>
                                        </Grid>
                                        <Grid xs={1} display="flex" justifyContent="center">
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.text.main }}>
                                                {item.concept || '-'}
                                            </Typography>
                                        </Grid>
                                        <Grid xs={1} display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", color: theme.vars.palette.text.main, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.reference}>
                                                {truncateReference(item.reference)}
                                            </Typography>
                                            {item.reference && (
                                                <IconButton size="sm" variant="plain" onClick={() => copyToClipboard(item.reference)} sx={{ minWidth: 24, minHeight: 24 }}>
                                                    <CopyIcon color={theme.vars.palette.text.primary} />
                                                </IconButton>
                                            )}
                                        </Grid>
                                    </Grid>
                                ))
                            ) : (
                                <Box display="flex" flexDirection="column" alignItems="center" mt={5}>
                                    <Typography level="body-md" sx={{ fontFamily: "Montserrat" }}>
                                        {depositsSearch ? `No se encontraron registros que coincidan con "${depositsSearch}"` : "No hay depósitos ni comisiones registrados."}
                                    </Typography>
                                    {depositsSearch && (
                                        <Typography level="body-sm" sx={{ mt: 1, color: theme.vars.palette.primary["500"], cursor: 'pointer', fontFamily: "Montserrat", '&:hover': { textDecoration: 'underline' } }} onClick={() => setDepositsSearch('')}>
                                            Limpiar búsqueda
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {!depositsLoadingState && depositsPagination.totalPages > 1 && (
                                <PaginationControls
                                    pagination={depositsPagination}
                                    currentPage={depositsPage}
                                    setPage={setDepositsPage}
                                />
                            )}
                        </Box>
                    </TabPanel>
                </Tabs>
            </Box>

            <Dialog title="Descargar reporte CSV" open={csvOpen} close={() => { if (!csvLoading) setCsvOpen(false); }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2, minWidth: 320 }}>
                    <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: theme.vars.palette.text.main }}>
                        Selecciona el rango de fechas. Si lo dejas vacío se incluye todo el historial.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 130 }}>
                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", fontWeight: 600, mb: 0.5, color: theme.vars.palette.text.main }}>Desde</Typography>
                            <Input type="date" value={csvStart} onChange={(e) => setCsvStart(e.target.value)}
                                slotProps={{ input: { style: { colorScheme: isDark ? 'dark' : 'light' } } }}
                                sx={{ borderRadius: "12px", border: "2px solid #18E9E9", fontFamily: "Montserrat", backgroundColor: theme.vars.palette.background.body, color: theme.vars.palette.text.main, '& input': { color: theme.vars.palette.text.main } }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 130 }}>
                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", fontWeight: 600, mb: 0.5, color: theme.vars.palette.text.main }}>Hasta</Typography>
                            <Input type="date" value={csvEnd} onChange={(e) => setCsvEnd(e.target.value)}
                                slotProps={{ input: { style: { colorScheme: isDark ? 'dark' : 'light' } } }}
                                sx={{ borderRadius: "12px", border: "2px solid #18E9E9", fontFamily: "Montserrat", backgroundColor: theme.vars.palette.background.body, color: theme.vars.palette.text.main, '& input': { color: theme.vars.palette.text.main } }} />
                        </Box>
                    </Box>
                    {csvError && (
                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: theme.vars.palette.danger[500] }}>
                            {csvError}
                        </Typography>
                    )}
                    <Button
                        color="primary"
                        loading={csvLoading}
                        disabled={csvLoading || (!!csvStart && !!csvEnd && csvStart > csvEnd)}
                        onClick={downloadCsv}
                        sx={{ borderRadius: "12px", fontFamily: "Montserrat", fontWeight: 600, mt: 1 }}
                    >
                        Descargar CSV
                    </Button>
                </Box>
            </Dialog>

            <Snackbar
                open={copySnack}
                autoHideDuration={1500}
                onClose={() => setCopySnack(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                variant="soft"
                color="success"
            >
                Copiado al portapapeles
            </Snackbar>
        </>
    );
};

export default TransactionsReportPage;